/**
 * What the agent does on the page with the viewer. Every action resolves its ref in an outline built now:
 *
 *   point   scroll to it, send the cogent's cursor there, and ring it for a few seconds
 *   open    expand it (a disclosure), and point at it
 *   press   point at it, then click it as a person would, with a flash; it asks the viewer first unless the page marks
 *           it safe
 *   go      follow it, when it is a link within this site
 *
 * All safety is enforced here, not in the prompt: page text can steer the model, and only the viewer's yes can press
 * something irreversible.
 */
import { cursorClick, cursorTo } from './orb/sandCursor'
import { sandRing } from './orb/sandRing'
import { outlineOf } from './outline'
import type { PageAction } from './protocol'

/** The cogent's own controls, including the buttons that answer it: it can never reach them. */
const OWN_CONTROLS = '[data-site-cogent], [data-testid=cogent-panel]'
/** With no markup saying otherwise, a link or disclosure labelled like this asks first too. */
const LOOKS_IRREVERSIBLE =
  /\b(delete|remove|reset|erase|destroy|submit|publish|release|pay|purchase|buy|sign out|log out|start over|send)\b/i

export type ActionResult = {
  kind: PageAction['kind']
  ref: string
  outcome: 'done' | 'declined' | 'missing' | 'off-site' | 'not-looking' | 'refused' | 'private'
  label: string
}

export type ActionHost = {
  /** The viewer's answer. Only a real click on the host's own prompt can say yes. */
  confirm: (question: string) => Promise<boolean>
  /** This page is not shared with the cogent, so it does nothing here. */
  privatePage: boolean
  /** The user closed the chat, which takes the page back: the cogent can say what it is doing, not act. */
  closed: () => boolean
}

const labelOf = (el: HTMLElement) =>
  (el.getAttribute('aria-label') ?? el.textContent ?? '').replace(/\s+/g, ' ').trim().slice(0, 100)

/** Marked by the page, or named like something that cannot be undone. */
const looksIrreversible = (el: HTMLElement) =>
  el.hasAttribute('data-irreversible') || LOOKS_IRREVERSIBLE.test(labelOf(el))

/** Brings `el` into view and rings it in the cogent's sand. */
function point(el: HTMLElement) {
  const still = matchMedia('(prefers-reduced-motion: reduce)').matches
  el.scrollIntoView({ block: 'center', behavior: still ? 'auto' : 'smooth' })
  sandRing(el)
  void cursorTo(el)
}

export async function runPageAction(action: PageAction, host: ActionHost): Promise<ActionResult> {
  const result = (outcome: ActionResult['outcome'], label = '') => ({
    kind: action.kind,
    ref: action.ref,
    outcome,
    label,
  })

  if (host.privatePage) return result('private')

  if (document.hidden || host.closed()) return result('not-looking')

  const el = outlineOf().refs.get(action.ref)

  if (!el) return result('missing')

  if (el.closest(OWN_CONTROLS)) return result('refused')

  const label = labelOf(el)
  const ask = () => host.confirm(`${action.note} Press "${label}"?`)
  // The cursor takes a moment to arrive, and the user may have left or closed the chat by then.
  const mayClick = async (target: HTMLElement) => {
    await cursorClick(target)
    return !document.hidden && !host.closed()
  }
  const link = el.closest('a[href]')

  if (
    (action.kind === 'go' || (action.kind === 'press' && link)) &&
    !(link instanceof HTMLAnchorElement && link.origin === location.origin)
  )
    return result('off-site', label)

  point(el)

  if (action.kind === 'point') return result('done', label)

  // An `e<n>` ref may name another element by the time the viewer answers, so the element they were asked about is
  // the one that is clicked, if it is still there.
  if (action.kind === 'open') {
    const summary = el.tagName === 'SUMMARY' ? (el.parentElement as HTMLDetailsElement) : null

    if (el.getAttribute('aria-expanded') === null && !summary) return result('refused', label)

    if (looksIrreversible(el) && !(await ask())) return result('declined', label)

    if (el.isConnected && (el.getAttribute('aria-expanded') === 'false' || summary?.open === false)) {
      if (!(await mayClick(el))) return result('not-looking', label)

      el.click()
    }

    return result('done', label)
  }

  if (action.kind === 'go') {
    const target = link as HTMLAnchorElement

    if (looksIrreversible(target) && !(await ask())) return result('declined', label)

    if (!target.isConnected) return result('missing', label)

    if (!(await mayClick(target))) return result('not-looking', label)

    target.focus({ preventScroll: true })
    target.click()
    return result('done', label)
  }

  // A press asks first unless the page says this control is safe to press unasked.
  const safe = el.hasAttribute('data-cogent-safe') && !looksIrreversible(el)

  if (!safe && !(await ask())) return result('declined', label)

  if (!el.isConnected) return result('missing', label)

  if (!(await mayClick(el))) return result('not-looking', label)

  el.focus({ preventScroll: true })
  el.click()
  return result('done', label)
}
